import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  QueryConstraint
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { Book, BookFormData, BookFilters } from '../types/book';
import { reviewService } from './reviewService';

export const bookService = {
  // Create a new book
  async createBook(bookData: BookFormData, userId: string): Promise<string> {
    let coverImageUrl = '';
    
    // Upload cover image if provided
    if (bookData.coverImage) {
      try {
        coverImageUrl = await this.uploadCoverImage(bookData.coverImage);
      } catch (error) {
        console.warn('Failed to upload cover image, proceeding without it:', error);
        // Continue without the image rather than failing the entire book creation
      }
    }

    const book = {
      ...bookData,
      coverImageUrl,
      addedBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
      averageRating: 0
    };

    // Remove coverImage from the data before saving
    const { coverImage, ...bookToSave } = book;
    
    const docRef = await addDoc(collection(db, 'books'), bookToSave);
    return docRef.id;
  },

  // Get all books with optional filtering
  async getBooks(filters?: BookFilters): Promise<Book[]> {
    let constraints: QueryConstraint[] = [];

    if (filters) {
      if (filters.genre && filters.genre !== 'all') {
        constraints.push(where('genre', '==', filters.genre));
      }
      if (filters.status && filters.status !== 'all') {
        constraints.push(where('status', '==', filters.status));
      }
      
      // Add ordering
      if (filters.sortBy) {
        const direction = filters.sortOrder === 'desc' ? 'desc' : 'asc';
        constraints.push(orderBy(filters.sortBy, direction));
      }
    }

    const q = query(collection(db, 'books'), ...constraints);
    const querySnapshot = await getDocs(q);
    
    let books = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as Book[];

    // Apply text search filter (client-side for simplicity)
    if (filters?.search) {
      const searchTerm = filters.search.toLowerCase();
      books = books.filter(book => 
        book.title.toLowerCase().includes(searchTerm) ||
        book.author.toLowerCase().includes(searchTerm) ||
        book.genre.toLowerCase().includes(searchTerm)
      );
    }

    return books;
  },

  // Get a single book by ID
  async getBookById(id: string): Promise<Book | null> {
    const docRef = doc(db, 'books', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as Book;
    }
    
    return null;
  },

  // Update a book
  async updateBook(id: string, bookData: Partial<BookFormData>): Promise<void> {
    let updateData: any = {
      ...bookData,
      updatedAt: new Date()
    };

    // Handle cover image update
    if (bookData.coverImage) {
      try {
        const coverImageUrl = await this.uploadCoverImage(bookData.coverImage);
        updateData.coverImageUrl = coverImageUrl;
      } catch (error) {
        console.warn('Failed to upload cover image, proceeding with update without new image:', error);
        // Continue without updating the image rather than failing the entire update
      }
    }

    // Remove coverImage from update data
    delete updateData.coverImage;

    const docRef = doc(db, 'books', id);
    await updateDoc(docRef, { ...updateData });
  },

  // Delete a book
  async deleteBook(id: string): Promise<void> {
    // Get book data to delete cover image
    const book = await this.getBookById(id);
    if (book?.coverImageUrl) {
      await this.deleteCoverImage(book.coverImageUrl);
    }

    // Delete associated reviews using reviewService
    await reviewService.deleteReviewsByBookId(id);

    // Delete book document
    const docRef = doc(db, 'books', id);
    await deleteDoc(docRef);
  },

  // Upload cover image to Firebase Storage
  async uploadCoverImage(file: File): Promise<string> {
    try {
      const timestamp = Date.now();
      const fileName = `book-covers/${timestamp}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error: any) {
      // Check if this is a storage setup issue
      if (error.code === 'storage/project-not-found' || 
          error.message?.includes('CORS') || 
          error.message?.includes('not have HTTP ok status')) {
        throw new Error('Firebase Storage is not set up. Please enable Firebase Storage in the Firebase Console first, or skip the image upload.');
      }
      throw error;
    }
  },

  // Delete cover image from Firebase Storage
  async deleteCoverImage(imageUrl: string): Promise<void> {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (error) {
      console.error('Error deleting cover image:', error);
    }
  },

  // Get books by genre (for analytics)
  async getBooksByGenre(): Promise<{genre: string, count: number}[]> {
    const books = await this.getBooks();
    const genreCounts: { [key: string]: number } = {};
    
    books.forEach(book => {
      genreCounts[book.genre] = (genreCounts[book.genre] || 0) + 1;
    });
    
    return Object.entries(genreCounts).map(([genre, count]) => ({
      genre,
      count
    }));
  },

  // Get books count by status
  async getBooksByStatus(): Promise<{status: string, count: number}[]> {
    const books = await this.getBooks();
    const statusCounts: { [key: string]: number } = {};
    
    books.forEach(book => {
      statusCounts[book.status] = (statusCounts[book.status] || 0) + 1;
    });
    
    return Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count
    }));
  },

  // Get top rated books
  async getTopRatedBooks(limitCount: number = 5): Promise<{title: string, author: string, rating: number}[]> {
    const q = query(
      collection(db, 'books'),
      where('averageRating', '>', 0),
      orderBy('averageRating', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        title: data.title,
        author: data.author,
        rating: data.averageRating
      };
    });
  }
}; 